/**
 * RootLayout — root route component.
 * Owns Nav, Footer, MerlinCallWidget, and site-global JSON-LD schemas.
 * Every Merlin CTA on the site is a plain <Link to="/intake"> — the single
 * front door. MerlinCallWidget is the floating FAB version of that link;
 * there is no modal and no booking state here.
 *
 * The /intake route is a full-bleed Merlin embed — Footer and MerlinCallWidget
 * are suppressed there, but Nav stays so visitors can navigate back to the
 * site without the browser back button. IntakePage pads for the fixed Nav.
 */
import { Outlet, useLocation } from '@tanstack/react-router';
import Nav from './sections/Nav.jsx';
import Footer from './sections/Footer.jsx';
import SchemaJsonLd from './primitives/SchemaJsonLd.jsx';
import MerlinCallWidget from './primitives/merlin/MerlinCallWidget.jsx';
import {
  organizationSchema,
  personSchema,
  serviceFoundrySchema,
  serviceAutomatedSchema,
  serviceTrainingSchema,
  localBusinessSchema,
  webSiteSchema,
} from '../content/schemas.js';

const FULL_BLEED_PREFIXES = ['/intake'];

function isFullBleedPath(pathname) {
  // Strip trailing slash so '/intake' and '/intake/' both match.
  const p = pathname.replace(/\/+$/, '') || '/';
  return FULL_BLEED_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  );
}

export default function RootLayout() {
  const { pathname } = useLocation();
  const fullBleed = isFullBleedPath(pathname);

  return (
    <>
      <Nav hideCta={fullBleed} />
      <main id="top">
        <Outlet />
      </main>
      {!fullBleed && <Footer />}
      {!fullBleed && <MerlinCallWidget />}

      {/* Site-global JSON-LD schemas */}
      <SchemaJsonLd data={organizationSchema} />
      <SchemaJsonLd data={personSchema} />
      <SchemaJsonLd data={serviceFoundrySchema} />
      <SchemaJsonLd data={serviceAutomatedSchema} />
      <SchemaJsonLd data={serviceTrainingSchema} />
      <SchemaJsonLd data={localBusinessSchema} />
      <SchemaJsonLd data={webSiteSchema} />
    </>
  );
}
